import { query, type RoutePreloadFuncArgs } from "@solidjs/router";
import { fetchThroughRestart } from "../lib/restart-retry";
import { fetchServerConfig } from "./Room.data";
import type { SceneElements } from "./SceneElementsTypes";
import type { TenantConfig } from "./TenantTypes";
import { setBaseURLForAssets } from "./signals";
export { absoluteURLForAsset } from "./urlUtils";

function getBaseURL(sceneElements: SceneElements, basePath: string) {
  let baseURL;
  if (basePath.endsWith("/")) basePath = basePath.slice(0, -1);
  if (sceneElements.baseURL) {
    baseURL = sceneElements.baseURL;
    if (baseURL === "../..") {
      baseURL = basePath.split("/").slice(0, -2).join("/");
    }
  } else {
    baseURL = basePath;
  }
  if (!baseURL.endsWith("/")) baseURL += "/";
  return baseURL;
}

const defaultConfig: TenantConfig = { rooms: [], scenes: [], mode: "project" };

export const fetchTenantConfig = query(async (tenantId: string): Promise<TenantConfig> => {
  if (!tenantId) return { ...defaultConfig };
  const response = await fetchThroughRestart(`/api/organizations/slug/${tenantId}/config`);
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(`notfound ${response.url}`);
    } else {
      throw new Error("Error fetching organization config");
    }
  }
  const results = await response.json();
  return { ...defaultConfig, ...results };
}, "tenantConfig");

export const fetchSceneElements = query(
  async (sceneName: string, tenantId: string | undefined): Promise<SceneElements> => {
    if (!sceneName) {
      return {
        components: {
          "environment-settings": {
            toneMapping: "NeutralToneMapping",
            backgroundColor: "#cccccc",
            envMapTexture: "/scenes/photo_studio_broadway_hall_1k.hdr",
          },
        },
        // libraries: [{ name: "Items", url: "./library.json" }],
        options: {
          useInstancedMesh: false,
          roomSwitcher: false,
          editor: {
            transformComponents: true,
            transformComponentsScale: true,
            floorAndWalls: false,
            measurements: true,
            raytracingRender: false,
            qrcodeScene: false,
            qrcodeSelectedEntity: false,
            exportPDF: false,
            exportGLTF: false,
            exportJSON: false,
          },
        },
      };
    }
    let scene;
    if (tenantId) {
      const scenesJson = await fetchTenantConfig(tenantId);
      scene = scenesJson.scenes.find((s) => s.sceneName === sceneName);
      if (!scene) {
        throw new Error(`notfound ${sceneName}`);
      }
    }

    let basePath;
    if (tenantId && scene) {
      basePath = `/scenes/${tenantId}/scenes/${scene.sceneName}/`;
    } else {
      basePath = `/scenes/${sceneName}/`;
    }
    const url = `${basePath}elements.json`;
    const res = await fetch(url);

    if (!res.ok) {
      if (res.status === 404) {
        throw new Error(`notfound ${url}`);
      } else {
        throw new Error("Error fetching elements.json");
      }
    }

    const sceneElements = await res.json();
    const baseURL = getBaseURL(sceneElements, basePath);
    console.log("baseURLForAssets set relative to elements.json", baseURL);
    setBaseURLForAssets(baseURL);
    return sceneElements;
  },
  "sceneElements"
);

export function preloadSceneElements({ params }: RoutePreloadFuncArgs) {
  fetchSceneElements(params.sceneName!, params.tenantId);
}

export function preloadSceneElementsAndServerConfig({ params }: RoutePreloadFuncArgs) {
  fetchSceneElements(params.sceneName!, params.tenantId);
  fetchServerConfig();
}

export function preloadServerConfig({ params }: RoutePreloadFuncArgs) {
  fetchServerConfig();
}

export function preloadTenantConfig({ params }: RoutePreloadFuncArgs) {
  fetchTenantConfig(params.tenantId!);
}
